import type {
  AgentSubject,
  ConversationListItem,
  IndexResponse,
  SseEvent,
} from './types';

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content') || ''
  );
}

export async function fetchAgentMessages(
  href: string,
  conversationId?: number | null
): Promise<IndexResponse> {
  const url = new URL(href, window.location.origin);
  if (conversationId) {
    url.searchParams.set('conversation_id', String(conversationId));
  }
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Fetch agent messages failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchAgentConversations(
  agentType: string,
  subject?: AgentSubject | null,
  limit = 20
): Promise<ConversationListItem[]> {
  const url = new URL('/agent_conversations', window.location.origin);
  url.searchParams.set('agent_type', agentType);
  url.searchParams.set('limit', String(limit));
  if (subject) {
    url.searchParams.set('subject_type', subject.type);
    url.searchParams.set('subject_id', String(subject.id));
  }
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Fetch conversations failed: ${res.status}`);
  }
  const body = await res.json();
  return body.conversations ?? [];
}

export async function renameAgentConversation(
  conversationId: number,
  title: string
): Promise<ConversationListItem> {
  const res = await fetch(`/agent_conversations/${conversationId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ conversation: { title } }),
  });
  if (!res.ok) {
    throw new Error(`Rename failed: ${res.status}`);
  }
  return res.json();
}

export async function deleteAgentConversation(
  conversationId: number
): Promise<void> {
  const res = await fetch(`/agent_conversations/${conversationId}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Delete failed: ${res.status}`);
  }
}

/**
 * What the page holds that the server has not seen.
 *
 * `reported` is the load-bearing field, and it is separate from `operations` on purpose:
 * an absent payload and an empty one mean different things. A page with no unsaved edits
 * sends `reported: true` with nothing in it; a page that cannot report its edits at all
 * — an older client, a host that has not been wired up — sends `reported: false`, and the
 * agent then knows it is reasoning from the saved run rather than assuming the screen is
 * clean. Without that distinction the two are identical on the wire, and the failure is
 * silent in the direction that stages a change onto the wrong properties.
 */
export type AgentPendingState = {
  reported: boolean;
  /** Pending override operations, in the shape the server already stores them. */
  operations?: unknown[];
  /**
   * Run-level settings the page is holding but has not calculated. Not slot overrides, so
   * the operations log says nothing about them — and without these, staging a change of
   * evaluation case left every later tool reading the old one.
   */
  settings?: {
    scenario_id?: number | null;
    evaluation_case_id?: number | null;
    dca_scenario_id?: number | null;
  };
};

export async function streamAgent(
  href: string,
  payload: {
    message: string;
    conversation_id?: number | null;
    new?: boolean;
    pending_state?: AgentPendingState;
  },
  onEvent: (evt: SseEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const url = new URL(href, window.location.origin);
  if (payload.conversation_id) {
    url.searchParams.set('conversation_id', String(payload.conversation_id));
  }
  if (payload.new) {
    url.searchParams.set('new', 'true');
  }

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({
      message: payload.message,
      ...(payload.pending_state
        ? { pending_state: payload.pending_state }
        : {}),
    }),
    credentials: 'same-origin',
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Agent request failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    // Stop reading as soon as the caller aborts; the request itself is already
    // aborted by fetch, this just ends the loop promptly.
    if (signal?.aborted) {
      await reader.cancel();
      return;
    }
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseSseEvent(raw);
      if (parsed) onEvent(parsed);
    }
  }
}

export function parseSseEvent(raw: string): SseEvent | null {
  let event: string | null = null;
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!event || !data) return null;
  try {
    return { event, data: JSON.parse(data) } as SseEvent;
  } catch {
    return null;
  }
}
